import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { siteContent } from "@/content/site";

const { navbar, brand } = siteContent;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F4FAF8]/85 backdrop-blur-xl border-b border-[#C9DDD6]/70 shadow-[0_4px_24px_rgba(15,56,32,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <button
            data-testid="nav-logo"
            onClick={() => handleClick("top")}
            className="flex items-center gap-2.5 group"
          >
            <img
              src={brand.logoUrl}
              alt={brand.logoAlt}
              className="w-10 h-10 lg:w-11 lg:h-11 rounded-2xl object-cover shadow-sm group-hover:rotate-3 transition-transform duration-300"
            />
            <div className="flex flex-col items-start leading-none">
              <span className="font-display font-extrabold text-[#1F4E5F] text-lg tracking-tight">
                {brand.name}
              </span>
              <span className="text-[10px] font-bold tracking-[0.16em] text-[#E8A33D] uppercase">
                {brand.subname}
              </span>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {navbar.links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => handleClick(l.id)}
                className="px-4 py-2 text-sm font-medium text-[#3D5560] hover:text-[#1F4E5F] rounded-full hover:bg-[#E1EEEA] transition-colors"
              >
                {l.label}
              </button>
            ))}
            <Link
              data-testid="nav-link-blog"
              to="/blog"
              className="px-4 py-2 text-sm font-medium text-[#3D5560] hover:text-[#1F4E5F] rounded-full hover:bg-[#E1EEEA] transition-colors"
            >
              {navbar.blogLabel}
            </Link>
            <Link
              data-testid="nav-link-faq"
              to="/faq"
              className="px-4 py-2 text-sm font-medium text-[#3D5560] hover:text-[#1F4E5F] rounded-full hover:bg-[#E1EEEA] transition-colors"
            >
              FAQ
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-beta"
              onClick={() => handleClick("beta")}
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#1F4E5F] text-[#F4FAF8] hover:bg-[#173E50] px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:gap-2.5 shadow-sm"
            >
              {navbar.cta} <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-[#E1EEEA] text-[#1F4E5F]"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-[#F4FAF8]/98 backdrop-blur-xl border-b border-[#C9DDD6]"
            data-testid="nav-mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {navbar.links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => handleClick(l.id)}
                  className="text-left px-4 py-3 rounded-xl text-[#0E2A36] hover:bg-[#E1EEEA] font-medium"
                >
                  {l.label}
                </button>
              ))}
              <Link
                data-testid="nav-mobile-link-blog"
                to="/blog"
                onClick={() => setOpen(false)}
                className="text-left px-4 py-3 rounded-xl text-[#0E2A36] hover:bg-[#E1EEEA] font-medium"
              >
                {navbar.blogLabel}
              </Link>
              <Link
                data-testid="nav-mobile-link-faq"
                to="/faq"
                onClick={() => setOpen(false)}
                className="text-left px-4 py-3 rounded-xl text-[#0E2A36] hover:bg-[#E1EEEA] font-medium"
              >
                FAQ
              </Link>
              <button
                data-testid="nav-mobile-cta-beta"
                onClick={() => handleClick("beta")}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#1F4E5F] text-[#F4FAF8] px-5 py-3 rounded-full text-sm font-semibold"
              >
                {navbar.cta} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
